import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchOrganizationOnboarding,
  patchOrganizationOnboarding,
  restartOrganizationOnboarding,
} from '../api/onboarding';
import type {
  OnboardingStepId,
  OrganizationOnboardingResponse,
  OrganizationOnboardingUpdate,
} from '../models/organization';
import { getStepMeta } from '../components/onboarding/onboardingCopy';
import {
  nextGuideStep,
  routeForStep,
  stepSatisfiedByCounts,
} from '../components/onboarding/onboardingSteps';

export type AdvanceTrigger = 'product' | 'client' | 'order' | 'approve';

export type OnboardingPendingAction =
  | 'open-approve'
  | 'open-create-order'
  | null;

const TRIGGER_TO_NEXT: Record<AdvanceTrigger, OnboardingStepId> = {
  product: 'client',
  client: 'order',
  order: 'approve',
  approve: 'done',
};

export function useOnboardingProgress(organizationId: string | undefined) {
  const [data, setData] = useState<OrganizationOnboardingResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [celebrateOpen, setCelebrateOpen] = useState(false);
  const [pendingAction, setPendingAction] =
    useState<OnboardingPendingAction>(null);
  const [stepFlashKey, setStepFlashKey] = useState(0);
  const orgRef = useRef(organizationId);
  orgRef.current = organizationId;
  const prevStepRef = useRef<OnboardingStepId | null>(null);
  const actionLockRef = useRef(false);

  const withActionLock = useCallback(async <T,>(fn: () => Promise<T>): Promise<T | null> => {
    if (actionLockRef.current) return null;
    actionLockRef.current = true;
    setActionBusy(true);
    try {
      return await fn();
    } finally {
      actionLockRef.current = false;
      setActionBusy(false);
    }
  }, []);

  const reload = useCallback(async () => {
    const id = orgRef.current;
    if (!id) {
      setData(null);
      return null;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetchOrganizationOnboarding(id);
      setData(res);
      if (
        res.onboarding &&
        res.onboarding.status === 'active' &&
        !res.onboarding.replay &&
        res.counts.approvedOrders >= 1 &&
        !res.onboarding.celebratedAt &&
        res.derivedCurrentStepId === 'done'
      ) {
        setCelebrateOpen(true);
      }
      return res;
    } catch (e) {
      const message =
        e instanceof Error ? e.message : 'No se pudo cargar el progreso';
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!organizationId) {
      setData(null);
      return;
    }
    void reload();
  }, [organizationId, reload]);

  const patch = useCallback(async (update: OrganizationOnboardingUpdate) => {
    const id = orgRef.current;
    if (!id) return null;
    try {
      const res = await patchOrganizationOnboarding(id, update);
      setData(res);
      return res;
    } catch (e) {
      console.error('Failed to patch onboarding', e);
      return null;
    }
  }, []);

  const showTour = Boolean(data?.showTour);
  const isDismissed = data?.onboarding?.status === 'dismissed';
  const isCompleted = data?.onboarding?.status === 'completed';
  const isReplay = Boolean(data?.onboarding?.replay);
  const canActivate =
    Boolean(organizationId) &&
    !showTour &&
    data !== null &&
    (data.onboarding == null ||
      data.onboarding.status === 'dismissed' ||
      data.onboarding.status === 'completed' ||
      data.isComplete);
  const currentStepId = data?.derivedCurrentStepId ?? null;
  const currentMeta = useMemo(
    () => getStepMeta(currentStepId),
    [currentStepId]
  );
  const currentSatisfied = stepSatisfiedByCounts(currentStepId, data?.counts);

  useEffect(() => {
    if (currentStepId && currentStepId !== prevStepRef.current) {
      prevStepRef.current = currentStepId;
      setStepFlashKey(k => k + 1);
    }
  }, [currentStepId]);

  const optimisticActive = useCallback(
    (step: OnboardingStepId, replay: boolean) => {
      setData(prev => {
        const counts = prev?.counts ?? {
          products: 0,
          clients: 0,
          orders: 0,
          approvedOrders: 0,
        };
        const ending = step === 'done' && !replay;
        return {
          onboarding: {
            version: prev?.onboarding?.version ?? 1,
            status: ending ? 'completed' : 'active',
            currentStepId: step,
            replay: ending ? false : replay,
            celebratedAt: ending
              ? new Date().toISOString()
              : prev?.onboarding?.celebratedAt ?? null,
            dismissedAt: null,
            startedAt: prev?.onboarding?.startedAt ?? new Date().toISOString(),
          },
          steps: prev?.steps ?? [],
          derivedCurrentStepId: step,
          isComplete: ending,
          showTour: !ending,
          counts,
        };
      });
      setStepFlashKey(k => k + 1);
    },
    []
  );

  const start = useCallback(async () => {
    return withActionLock(async () => {
      // Idempotent: already past intro
      if (currentStepId && currentStepId !== 'intro') {
        return data;
      }
      optimisticActive('product', isReplay);
      return patch({
        currentStepId: 'product',
        status: 'active',
        replay: isReplay,
      });
    });
  }, [
    withActionLock,
    currentStepId,
    data,
    optimisticActive,
    isReplay,
    patch,
  ]);

  const goNext = useCallback(async () => {
    return withActionLock(async () => {
      const next = nextGuideStep(currentStepId);
      if (next === currentStepId) return data;
      if (next === 'approve') {
        setPendingAction('open-approve');
      }
      if (next === 'done') {
        // End guide (first-run celebrate, replay just close)
        setPendingAction(null);
        if (!isReplay) setCelebrateOpen(true);
        optimisticActive('done', false);
        return patch({
          status: 'completed',
          currentStepId: 'done',
          replay: false,
          celebratedAt: new Date().toISOString(),
        });
      }
      optimisticActive(next, isReplay);
      return patch({
        currentStepId: next,
        status: 'active',
        replay: isReplay,
      });
    });
  }, [
    withActionLock,
    currentStepId,
    data,
    optimisticActive,
    isReplay,
    patch,
  ]);

  const dismiss = useCallback(async () => {
    return withActionLock(async () => {
      setPendingAction(null);
      setData(prev =>
        prev
          ? {
              ...prev,
              showTour: false,
              onboarding: prev.onboarding
                ? { ...prev.onboarding, status: 'dismissed' }
                : prev.onboarding,
            }
          : prev
      );
      return patch({ status: 'dismissed', replay: false });
    });
  }, [withActionLock, patch]);

  const resume = useCallback(async () => {
    return withActionLock(async () => {
      setData(prev =>
        prev
          ? {
              ...prev,
              showTour: true,
              isComplete: false,
              onboarding: prev.onboarding
                ? {
                    ...prev.onboarding,
                    status: 'active',
                    dismissedAt: null,
                    replay: false,
                  }
                : prev.onboarding,
            }
          : prev
      );
      setStepFlashKey(k => k + 1);
      await patch({ status: 'active', dismissedAt: null, replay: false });
      return reload();
    });
  }, [withActionLock, patch, reload]);

  const activate = useCallback(async () => {
    return withActionLock(async () => {
      const id = orgRef.current;
      if (!id) return null;
      // Immediate feedback: always from intro in replay
      optimisticActive('intro', true);
      try {
        const res = await restartOrganizationOnboarding(id);
        setData(res);
        setCelebrateOpen(false);
        setPendingAction(null);
        setStepFlashKey(k => k + 1);
        return res;
      } catch (e) {
        console.error('Failed to restart onboarding', e);
        await reload();
        return null;
      }
    });
  }, [withActionLock, optimisticActive, reload]);

  const markCelebrated = useCallback(async () => {
    return withActionLock(async () => {
      setCelebrateOpen(false);
      setPendingAction(null);
      setData(prev =>
        prev
          ? {
              ...prev,
              showTour: false,
              isComplete: true,
              onboarding: prev.onboarding
                ? {
                    ...prev.onboarding,
                    status: 'completed',
                    currentStepId: 'done',
                    replay: false,
                  }
                : prev.onboarding,
            }
          : prev
      );
      return patch({
        status: 'completed',
        currentStepId: 'done',
        replay: false,
        celebratedAt: new Date().toISOString(),
      });
    });
  }, [withActionLock, patch]);

  const clearPendingAction = useCallback(() => {
    setPendingAction(null);
  }, []);

  const requestOpenApprove = useCallback(() => {
    setPendingAction('open-approve');
  }, []);

  const requestOpenCreateOrder = useCallback(() => {
    setPendingAction('open-create-order');
  }, []);

  /** Merge list-page evidence so skip works even if server counts lag (takes max). */
  const reportLiveCounts = useCallback(
    (partial: Partial<{
      products: number;
      clients: number;
      orders: number;
      approvedOrders: number;
    }>) => {
      setData(prev => {
        if (!prev) return prev;
        const nextCounts = { ...prev.counts };
        let changed = false;
        (Object.keys(partial) as Array<keyof typeof partial>).forEach(key => {
          const incoming = partial[key];
          if (typeof incoming !== 'number') return;
          const merged = Math.max(prev.counts[key] ?? 0, incoming);
          if (merged !== prev.counts[key]) {
            nextCounts[key] = merged;
            changed = true;
          }
        });
        if (!changed) return prev;
        return { ...prev, counts: nextCounts };
      });
    },
    []
  );

  const advanceAfterMutation = useCallback(
    async (trigger: AdvanceTrigger) => {
      return withActionLock(async () => {
        const next = TRIGGER_TO_NEXT[trigger];
        const stepNow = currentStepId;

        // Idempotent: already at/past the destination
        if (stepNow === next || stepNow === 'done') {
          if (trigger === 'order' && stepNow === 'approve') {
            setPendingAction('open-approve');
          }
          if (trigger === 'approve' && stepNow === 'done') {
            if (!isReplay) setCelebrateOpen(true);
            else {
              // Replay finish if we landed on done without completing
              await patch({
                status: 'completed',
                currentStepId: 'done',
                replay: false,
                celebratedAt: new Date().toISOString(),
              });
            }
          }
          return { nextStepId: next, response: data };
        }

        // Create sale → move to approve and open review (set action AFTER optimistic UI)
        if (trigger === 'order') {
          optimisticActive('approve', isReplay);
          const res = await patch({
            currentStepId: 'approve',
            status: 'active',
            replay: isReplay,
          });
          setPendingAction('open-approve');
          return { nextStepId: 'approve', response: res };
        }

        // Approve → finish tour (do not reload; Firestore can briefly return stale step)
        if (trigger === 'approve') {
          setPendingAction(null);
          if (!isReplay) setCelebrateOpen(true);
          optimisticActive('done', false);
          const res = await patch({
            status: 'completed',
            currentStepId: 'done',
            replay: false,
            celebratedAt: new Date().toISOString(),
          });
          return { nextStepId: 'done', response: res };
        }

        optimisticActive(next, isReplay);
        const res = await patch({
          currentStepId: next,
          status: 'active',
          replay: isReplay,
        });
        return { nextStepId: next, response: res };
      });
    },
    [withActionLock, currentStepId, data, optimisticActive, isReplay, patch]
  );

  return {
    data,
    loading,
    actionBusy,
    error,
    showTour,
    isDismissed,
    isCompleted,
    isReplay,
    canActivate,
    isComplete: Boolean(data?.isComplete),
    steps: data?.steps ?? [],
    counts: data?.counts,
    currentStepId,
    currentMeta,
    currentSatisfied,
    celebrateOpen,
    setCelebrateOpen,
    pendingAction,
    clearPendingAction,
    requestOpenApprove,
    requestOpenCreateOrder,
    reportLiveCounts,
    stepFlashKey,
    reload,
    start,
    goNext,
    dismiss,
    resume,
    activate,
    markCelebrated,
    advanceAfterMutation,
    routeForStep,
    patch,
  };
}

export type OnboardingProgressApi = ReturnType<typeof useOnboardingProgress>;
